import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  TextField,
  Button,
  DialogActions,
  Box,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { Field, Formik, FormikHelpers } from 'formik';
import { firestore } from '../../../../../../libs/firebase-service/src';
import { useSelector } from 'react-redux';
import { setUser } from '../../../../../../libs/redux-slices/src';
import { useDispatch } from 'react-redux';
import { environment } from '../../../environments/environment';
import { useTranslation } from 'react-i18next';

const ChangeName = ({
  open,
  onClose,
  setName,
  name,
  editingField = 'both',
}) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.authReducer.token);
  const user = useSelector((state) => state.authReducer.userData);
  const [userData, setUserData] = useState([]);
  useEffect(() => {
    setUserData(user);
  }, [user]);
  const handleSubmit = async (values) => {
    setLoading(true);
    const firstName = values.firstName?.trim() || '';
    const lastName = values.lastName?.trim() || '';

    // Capitalize first letter of each name
    const formattedFirstName =
      firstName.charAt(0).toUpperCase() + firstName.slice(1);
    const formattedLastName =
      lastName.charAt(0).toUpperCase() + lastName.slice(1);
    const fullName = `${formattedFirstName} ${formattedLastName}`.trim();

    const updateDoc = await firestore.collection('restaurant').doc();
    setName(fullName);

    if (fullName.length < 40) {
      await firestore
        .collection('users')
        .doc(userData?.uid)
        .set(
          {
            firstName: formattedFirstName,
            lastName: formattedLastName,
            fullName: fullName,
          },
          { merge: true }
        )
        .then(() => {
          onClose();
          setLoading(false);
          const updatedUser = {
            ...userData,
            firstName: formattedFirstName,
            lastName: formattedLastName,
            fullName: fullName,
          };
          dispatch(setUser({ userData: updatedUser }));
          onClose();
        })
        .catch((e) => {
          setLoading(false);
          onClose();
        });
      await firestore
        .collection('restaurant')
        .doc('Restaurant')
        .set(
          {
            update_items_id: updateDoc.id,
          },
          { merge: true }
        )
        .then(() => {
          onClose();
        })
        .catch((e) => {
          console.log('err:', e);
        });
      onClose();
    }
  };

  const [t, i18n] = useTranslation();

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={() => onClose()}
      aria-labelledby="change-name-dialog"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      {/* Header with icon and close button */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${environment.mainColor}20, ${environment.mainColor}10)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonIcon
                sx={{
                  color: environment.mainColor,
                  fontSize: 24,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '20px',
                  color: '#172B4D',
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                {editingField === 'firstName'
                  ? 'Change First Name'
                  : editingField === 'lastName'
                  ? 'Change Last Name'
                  : t('profilee.changeName')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  fontSize: '14px',
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                {editingField === 'firstName'
                  ? 'Update your first name'
                  : editingField === 'lastName'
                  ? 'Update your last name'
                  : 'Update your display name'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => onClose()}
            sx={{
              color: '#6B7280',
              '&:hover': {
                backgroundColor: '#F3F4F6',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Formik
          enableReinitialize
          initialValues={{
            firstName: userData?.firstName || '',
            lastName: userData?.lastName || '',
          }}
          onSubmit={handleSubmit}
        >
          {({
            handleBlur,
            handleChange,
            values,
            errors,
            handleSubmit,
            touched,
          }) => (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                {(editingField === 'firstName' || editingField === 'both') && (
                  <Grid item xs={12} sm={editingField === 'both' ? 6 : 12}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      placeholder="Enter your first name"
                      onChange={handleChange('firstName')}
                      onBlur={handleBlur('firstName')}
                      required
                      value={values?.firstName}
                      autoComplete="given-name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#F9FAFB',
                          fontFamily: 'Jost, sans-serif',
                          '& fieldset': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover fieldset': {
                            borderColor: environment.mainColor,
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'white',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: environment.mainColor,
                            borderWidth: '2px',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 500,
                          '&.Mui-focused': {
                            color: environment.mainColor,
                          },
                        },
                      }}
                      variant="outlined"
                    />
                  </Grid>
                )}
                {(editingField === 'lastName' || editingField === 'both') && (
                  <Grid item xs={12} sm={editingField === 'both' ? 6 : 12}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      placeholder="Enter your last name"
                      onChange={handleChange('lastName')}
                      onBlur={handleBlur('lastName')}
                      required
                      value={values?.lastName}
                      autoComplete="family-name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#F9FAFB',
                          fontFamily: 'Jost, sans-serif',
                          '& fieldset': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover fieldset': {
                            borderColor: environment.mainColor,
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'white',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: environment.mainColor,
                            borderWidth: '2px',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 500,
                          '&.Mui-focused': {
                            color: environment.mainColor,
                          },
                        },
                      }}
                      variant="outlined"
                    />
                  </Grid>
                )}
              </Grid>

              {/* Error messages with better styling */}
              {(((errors.firstName || errors.lastName) &&
                (touched.firstName || touched.lastName)) ||
                (values?.firstName?.length > 0 &&
                  values?.firstName?.replace(/ /g, '')?.length === 0) ||
                (values?.lastName?.length > 0 &&
                  values?.lastName?.replace(/ /g, '')?.length === 0)) && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    backgroundColor: '#FEF2F2',
                    borderRadius: '8px',
                    border: '1px solid #FCA5A5',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#DC2626',
                      fontSize: '13px',
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    {(values?.firstName?.length > 0 &&
                      values?.firstName?.replace(/ /g, '')?.length === 0) ||
                    (values?.lastName?.length > 0 &&
                      values?.lastName?.replace(/ /g, '')?.length === 0)
                      ? t('profilee.pleaseEnterAValidName')
                      : errors.firstName || errors.lastName}
                  </Typography>
                </Box>
              )}

              {(values?.firstName?.length || 0) +
                (values?.lastName?.length || 0) >
                40 && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    backgroundColor: '#FEF2F2',
                    borderRadius: '8px',
                    border: '1px solid #FCA5A5',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#DC2626',
                      fontSize: '13px',
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    {t('profilee.fullNameShouldnotBeMore20')}
                  </Typography>
                </Box>
              )}

              {/* Character count indicator */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#6B7280',
                    fontFamily: 'Jost, sans-serif',
                  }}
                >
                  {values?.fullName?.length > 0 && (
                    <>{values?.fullName?.length}/20 characters</>
                  )}
                </Typography>
              </Box>

              {/* Action buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'flex-end',
                  mt: 4,
                }}
              >
                <Button
                  onClick={() => onClose()}
                  variant="outlined"
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    fontSize: '15px',
                    padding: '10px 24px',
                    borderColor: '#E5E7EB',
                    color: '#374151',
                    '&:hover': {
                      borderColor: '#D1D5DB',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  {t('profilee.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={
                    loading ||
                    (values?.firstName?.replace(/ /g, '')?.length === 0 &&
                      values?.lastName?.replace(/ /g, '')?.length === 0) ||
                    (values?.firstName?.length || 0) +
                      (values?.lastName?.length || 0) >
                      40
                  }
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                    ) : null
                  }
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    fontSize: '15px',
                    padding: '10px 24px',
                    backgroundColor:
                      loading ||
                      (values?.firstName?.replace(/ /g, '')?.length === 0 &&
                        values?.lastName?.replace(/ /g, '')?.length === 0) ||
                      (values?.firstName?.length || 0) +
                        (values?.lastName?.length || 0) >
                        40
                        ? '#D1D5DB'
                        : environment.mainColor,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor:
                        loading ||
                        (values?.firstName?.replace(/ /g, '')?.length === 0 &&
                          values?.lastName?.replace(/ /g, '')?.length === 0) ||
                        (values?.firstName?.length || 0) +
                          (values?.lastName?.length || 0) >
                          40
                          ? '#D1D5DB'
                          : environment.mainColor,
                      opacity: 0.9,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                    '&:disabled': {
                      backgroundColor: '#D1D5DB',
                      color: 'white',
                    },
                  }}
                >
                  {loading ? 'Saving...' : t('profilee.saveInfo')}
                </Button>
              </Box>
            </Box>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeName;
